import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { useSelectedSite } from '/src/hooks';
import { AUDIENCE_MODES } from './constants';
import { AUDIENCE_METADATA } from './metadata';
import { definitionToDraft, formatAudienceSummary } from './utils';

export function useAudienceSummary(definition, { siteId = null, mode = AUDIENCE_MODES.TARGETING } = {}) {
  const { selectedSite } = useSelectedSite();
  const asyncCriteria = useMemo(() => getAsyncSummaryCriteria(definition), [definition]);
  const effectiveSiteId = normalizeSiteId(siteId ?? selectedSite?.id);
  const queryContext = useMemo(
    () => ({
      selectedSite: effectiveSiteId ? { id: effectiveSiteId } : selectedSite,
    }),
    [effectiveSiteId, selectedSite],
  );
  const queries = useMemo(
    () =>
      asyncCriteria.map((criteria) => {
        const asyncOptions = AUDIENCE_METADATA[criteria].asyncOptions;
        const query = asyncOptions.query(queryContext);
        const enabled = (query.enabled ?? true) && (asyncOptions.enabled?.(queryContext) ?? true);

        return {
          ...query,
          enabled,
          useErrorBoundary: false,
        };
      }),
    [asyncCriteria, queryContext],
  );
  const queryResults = useQueries({
    queries,
  });
  const isLoadingLabels = useMemo(
    () =>
      asyncCriteria.length > 0 &&
      queryResults.some(
        (result, index) =>
          queries[index]?.enabled !== false && (result.isLoading || (result.isFetching && !result.data)),
      ),
    [asyncCriteria.length, queries, queryResults],
  );

  const optionLabelsByCriteria = useMemo(
    () =>
      Object.fromEntries(
        asyncCriteria.flatMap((criteria, index) => {
          const data = queryResults[index]?.data;

          if (!data) {
            return [];
          }

          const options = AUDIENCE_METADATA[criteria].asyncOptions.getOptions({ data, mode });

          return [[criteria, new Map(options.map((option) => [String(option.value), option.label]))]];
        }),
      ),
    [asyncCriteria, mode, queryResults],
  );
  const summary = useMemo(
    () => formatAudienceSummary(definition, { optionLabelsByCriteria }),
    [definition, optionLabelsByCriteria],
  );

  return useMemo(() => ({ summary, isLoadingLabels }), [summary, isLoadingLabels]);
}

function normalizeSiteId(siteId) {
  const parsedSiteId = Number(siteId);

  return Number.isInteger(parsedSiteId) && parsedSiteId > 0 ? parsedSiteId : null;
}

function getAsyncSummaryCriteria(definition) {
  const criteria = definitionToDraft(definition)
    .conditions.map((condition) => condition.criteria)
    .filter((criteria) => AUDIENCE_METADATA[criteria]?.type === 'asyncSelect');

  return [...new Set(criteria)];
}
